import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { gerarRelatorio, gerarRelatorioComparativo, secoesDisponiveis, temComparativo } from "./Gerador_relatorio";
import { PDFDownloadLink } from "@react-pdf/renderer";
import Modal from 'react-modal';
import api from '../service/api';

// Componentes Base
import Menu from './Menu';
import BlocoAluno from './Bloco_aluno';
import EditarAluno from './Editar_aluno';

import '../css/Home.css';
import '../css/Home_aluno.css';

function HomeAluno() {
    const location = useLocation();
    const navigate = useNavigate();

    const [modalConfirmacao, setModalConfirmacao] = useState(false);
    const [modalExcluirAluno, setModalExcluirAluno] = useState(false);
    const [modalGerarRelatorio, setModalGerarRelatorio] = useState('');
    const [modalCompararAvaliacao, setModalCompararAvaliacao] = useState('');

    const [avaliacaoParaExcluir, setAvaliacaoParaExcluir] = useState(null);
    const [excluindoAvaliacao, setExcluindoAvaliacao] = useState(false);
    const [erroExclusao, setErroExclusao] = useState('');
    const [excluindoAluno, setExcluindoAluno] = useState(false);
    const [erroExclusaoAluno, setErroExclusaoAluno] = useState('');

    const [blocoAvaliacoesRealizadas, setBlocoAvaliacoesRealizadas] = useState(true);
    const [isCadastroOpen, setIsCadastroOpen] = useState(false);
    const [alunoParaEditar, setAlunoParaEditar] = useState(null);
    const [secoesSelecionadas, setSecoesSelecionadas] = useState({});

    // Comparativo: ids das avaliações escolhidas (2 a 4) e os dados já carregados.
    const [comparacaoSelecionadas, setComparacaoSelecionadas] = useState([]);
    const [listaComparacaoAberta, setListaComparacaoAberta] = useState(false);
    const refListaComparacao = useRef(null);

    // Fecha a lista suspensa ao clicar fora dela.
    useEffect(() => {
        if (!listaComparacaoAberta) return;
        const aoClicar = (evento) => {
            if (refListaComparacao.current && !refListaComparacao.current.contains(evento.target)) {
                setListaComparacaoAberta(false);
            }
        };
        document.addEventListener('mousedown', aoClicar);
        return () => document.removeEventListener('mousedown', aoClicar);
    }, [listaComparacaoAberta]);
    const [dadosComparacao, setDadosComparacao] = useState(null);
    const [carregandoComparacao, setCarregandoComparacao] = useState(false);
    const [erroComparacao, setErroComparacao] = useState('');

    const alunoSelecionado = location.state?.alunoSelecionado || null;

    const [avaliacoes, setAvaliacoes] = useState(alunoSelecionado?.avaliacoesRealizadas || []);

    // CAP-5: dados carregados sob demanda quando o treinador clica "Gerar relatório".
    const [evaluationDataParaPDF, setEvaluationDataParaPDF] = useState(null);
    const [carregandoRelatorio, setCarregandoRelatorio] = useState(false);

    // Página "Análise e recomendações": gerada no servidor (IA com fallback por regras).
    const [resumoIA, setResumoIA] = useState(null);
    const [carregandoResumoIA, setCarregandoResumoIA] = useState(false);
    const [erroResumoIA, setErroResumoIA] = useState('');
    const personalConfig = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('@AppAcademia:user') || 'null');
        } catch (e) {
            return null;
        }
    }, []);

    // A foto do personal (URL do R2) é convertida em dataURL para embutir no PDF.
    // O @react-pdf/renderer renderiza dataURL de forma confiável (evita falhas de
    // CORS/timing ao buscar imagem remota durante a renderização).
    // null = conversão ainda pendente; o botão de download aguarda esse estado,
    // pois o PDFDownloadLink congela o documento na montagem e não regenera depois.
    const [personalParaPDF, setPersonalParaPDF] = useState(null);

    useEffect(() => {
        let cancelado = false;
        const fotoUrl = personalConfig?.photo;
        if (!fotoUrl || !/^https?:\/\//.test(fotoUrl)) {
            setPersonalParaPDF(personalConfig);
            return;
        }
        // cache: 'no-store' — evita reusar do cache HTTP a resposta da tag <img> do menu,
        // que foi obtida sem CORS e faria este fetch falhar mesmo com a política correta.
        fetch(fotoUrl, { cache: 'no-store' })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status} ao buscar a foto`);
                return r.blob();
            })
            .then((blob) => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            }))
            .then((dataUrl) => {
                if (!cancelado) setPersonalParaPDF({ ...personalConfig, photo: dataUrl });
            })
            .catch((err) => {
                // Se falhar, remove a foto — o relatório renderiza sem imagem.
                console.warn('Não foi possível carregar a foto do personal para o PDF:', err);
                if (!cancelado) setPersonalParaPDF({ ...personalConfig, photo: '' });
            });
        return () => { cancelado = true; };
    }, [personalConfig]);

    // Seções realizadas nesta avaliação (mesmos critérios das páginas do relatório).
    const secoesDaAvaliacao = useMemo(
        () => (evaluationDataParaPDF ? secoesDisponiveis(evaluationDataParaPDF, alunoSelecionado?.profile) : []),
        [evaluationDataParaPDF, alunoSelecionado?.profile]
    );

    // Ao carregar a avaliação, já vêm marcadas todas as etapas realizadas.
    useEffect(() => {
        const marcadas = {};
        secoesDaAvaliacao.forEach((s) => { if (s.disponivel) marcadas[s.chave] = true; });
        setSecoesSelecionadas(marcadas);
    }, [secoesDaAvaliacao]);

    useEffect(() => {
        if (carregandoResumoIA || resumoIA) return;
        setSecoesSelecionadas((prev) => (prev.AnaliseIA ? { ...prev, AnaliseIA: false } : prev));
    }, [carregandoResumoIA, resumoIA]);

    const chavesSelecionadas = useMemo(
        () => Object.keys(secoesSelecionadas).filter((chave) => secoesSelecionadas[chave]),
        [secoesSelecionadas]
    );

    // Pede a análise assim que a avaliação do modal estiver carregada (o servidor reaproveita a
    // análise anterior enquanto a avaliação não mudar, então a chamada costuma ser imediata).
    useEffect(() => {
        const evaluationId = typeof modalGerarRelatorio === 'string' ? modalGerarRelatorio : null;
        const alunoId = alunoSelecionado?.id;
        if (!evaluationId || !alunoId || !evaluationDataParaPDF) {
            setResumoIA(null);
            setErroResumoIA('');
            return;
        }
        let cancelado = false;
        setCarregandoResumoIA(true);
        setErroResumoIA('');
        api.post(`/api/student/${alunoId}/evaluation/${evaluationId}/resumo-ia`, {}, { withCredentials: true })
            .then((response) => {
                if (cancelado) return;
                if (response.data?.disponivel && response.data?.resumo) {
                    setResumoIA(response.data);
                } else {
                    // Sem resposta da IA a página não é gerada — não há texto substituto.
                    setResumoIA(null);
                    setErroResumoIA(`${response.data?.motivo || 'A análise automática não está disponível no momento.'} O relatório será gerado sem a página de análise.`);
                }
            })
            .catch((err) => {
                console.error('Erro ao gerar a análise da avaliação:', err);
                if (!cancelado) {
                    setResumoIA(null);
                    setErroResumoIA('Não foi possível gerar a análise automática agora. O relatório será gerado sem essa página.');
                }
            })
            .finally(() => { if (!cancelado) setCarregandoResumoIA(false); });
        return () => { cancelado = true; };
    }, [modalGerarRelatorio, alunoSelecionado?.id, evaluationDataParaPDF]);

    const documentoPDF = useMemo(() => {
        return gerarRelatorio({
            evaluationData: evaluationDataParaPDF,
            aluno: alunoSelecionado,
            personal: personalParaPDF,
            secoes: chavesSelecionadas,
            resumoIA,
        });
    }, [evaluationDataParaPDF, alunoSelecionado, personalParaPDF, chavesSelecionadas, resumoIA]);

    // --- Comparativo entre duas avaliações ---
    const avaliacoesOrdenadas = useMemo(
        () => [...avaliacoes].sort((x, y) => new Date(x.data) - new Date(y.data)),
        [avaliacoes]
    );

    // Busca as avaliações escolhidas e ordena da mais antiga para a mais recente.
    useEffect(() => {
        if (!modalCompararAvaliacao || comparacaoSelecionadas.length < 2) {
            setDadosComparacao(null);
            return;
        }
        let cancelado = false;
        setCarregandoComparacao(true);
        setErroComparacao('');
        Promise.all(comparacaoSelecionadas.map((id) =>
            api.get(`/api/student/${alunoSelecionado?.id}/evaluation/${id}`, { withCredentials: true })
        ))
            .then((respostas) => {
                if (cancelado) return;
                const lista = respostas.map((r) => r.data)
                    .sort((x, y) => new Date(x.evaluationDate) - new Date(y.evaluationDate));
                setDadosComparacao({ avaliacoes: lista });
            })
            .catch((err) => {
                console.error('Erro ao buscar avaliações para comparação:', err);
                if (!cancelado) {
                    setDadosComparacao(null);
                    setErroComparacao('Não foi possível carregar as avaliações selecionadas. Tente novamente.');
                }
            })
            .finally(() => { if (!cancelado) setCarregandoComparacao(false); });
        return () => { cancelado = true; };
    }, [modalCompararAvaliacao, comparacaoSelecionadas, alunoSelecionado?.id]);

    const documentoComparativo = useMemo(() => {
        if (!dadosComparacao) return null;
        return gerarRelatorioComparativo({
            avaliacoes: dadosComparacao.avaliacoes,
            aluno: alunoSelecionado,
            personal: personalParaPDF,
        });
    }, [dadosComparacao, alunoSelecionado, personalParaPDF]);

    const comparativoTemDados = useMemo(
        () => (dadosComparacao ? temComparativo(dadosComparacao.avaliacoes) : false),
        [dadosComparacao]
    );

    useEffect(() => {
        if (!alunoSelecionado) {
            navigate("/home");
        }
    }, [alunoSelecionado, navigate]);

    // CAP-1: ao cadastrar/trocar aluno via menu, re-sincroniza o painel de avaliações.
    // useState (linha 37) só inicializa no mount — sem este efeito, o painel mostra dados do aluno anterior.
    useEffect(() => {
        setAvaliacoes(alunoSelecionado?.avaliacoesRealizadas || []);
    }, [alunoSelecionado?.id]);

    // Busca a lista atualizada no servidor ao abrir a tela: uma avaliação recém-finalizada
    // aparece aqui sem precisar sair do aluno e entrar de novo.
    useEffect(() => {
        const alunoId = alunoSelecionado?.id;
        if (!alunoId) return;
        let cancelado = false;
        api.get('/api/user/students', { withCredentials: true })
            .then((response) => {
                if (cancelado) return;
                const atualizado = (response.data || []).find((a) => a.id === alunoId);
                if (atualizado) setAvaliacoes(atualizado.avaliacoesRealizadas || []);
            })
            .catch((err) => console.error('Erro ao atualizar as avaliações do aluno:', err));
        return () => { cancelado = true; };
    }, [alunoSelecionado?.id]);

    // CAP-5: quando o modal de relatório abre com um ID de avaliação, busca o doc completo do Firestore.
    useEffect(() => {
        const evaluationId = typeof modalGerarRelatorio === 'string' ? modalGerarRelatorio : null;
        const alunoId = alunoSelecionado?.id;
        if (!evaluationId || !alunoId) {
            setEvaluationDataParaPDF(null);
            return;
        }
        let cancelado = false;
        setCarregandoRelatorio(true);
        api.get(`/api/student/${alunoId}/evaluation/${evaluationId}`, { withCredentials: true })
            .then((response) => {
                if (!cancelado) setEvaluationDataParaPDF(response.data);
            })
            .catch((err) => {
                console.error('Erro ao buscar dados da avaliação para o relatório:', err);
                if (!cancelado) setEvaluationDataParaPDF(null);
            })
            .finally(() => {
                if (!cancelado) setCarregandoRelatorio(false);
            });
        return () => { cancelado = true; };
    }, [modalGerarRelatorio, alunoSelecionado?.id]);

    if (!alunoSelecionado) {
        return null;
    }

    const { name, id } = alunoSelecionado;

    function handleAvaliacoesRealizadasClick(id) {
        setBlocoAvaliacoesRealizadas(true);
    }

    function handleAbrirAvaliacao(id) {
        navigate(`/avaliacao/${id}`, { state: { alunoSelecionado, evaluationId: id } });
    }

    // Marca/desmarca uma etapa no modal de geração do relatório.
    const alternarSecao = (chave) => {
        setSecoesSelecionadas(prev => ({ ...prev, [chave]: !prev[chave] }));
    };

    function tratarDataString(dataString) {
        const data = new Date(dataString);
        const dia = String(data.getDate()).padStart(2, '0');
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const ano = data.getFullYear();
        return `${dia}/${mes}/${ano}`;
    }

    const handleFecharModalRelatorio = () => {
        setModalGerarRelatorio('');
    };

    const LIMITE_COMPARACAO = 4;

    const alternarAvaliacaoComparacao = (id) => {
        setComparacaoSelecionadas((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= LIMITE_COMPARACAO) return prev;
            return [...prev, id];
        });
    };

    const handleFecharModalComparacao = () => {
        setModalCompararAvaliacao(false);
        setListaComparacaoAberta(false);
        setComparacaoSelecionadas([]);
        setDadosComparacao(null);
        setErroComparacao('');
    };


    // Popup de cadastro para editar dados do aluno
    const handleAbrirEdicao = (aluno) => {
        setAlunoParaEditar(aluno);
        setIsCadastroOpen(true);
    };

    const handleExcluirAluno = () => {
        setErroExclusaoAluno('');
        setModalExcluirAluno(true);
    };

    const handleCompararAvaliacoes = () => {
        setComparacaoSelecionadas([]);
        setDadosComparacao(null);
        setErroComparacao('');
        setModalCompararAvaliacao(true);
    };

    const handleAbrirModalExclusao = (evaluationId) => {
        setAvaliacaoParaExcluir(evaluationId);
        setErroExclusao('');
        setModalConfirmacao(true);
    };

    const handleFecharModalExclusao = () => {
        if (excluindoAvaliacao) return;
        setModalConfirmacao(false);
        setAvaliacaoParaExcluir(null);
        setErroExclusao('');
    };

    const handleConfirmarExclusaoAvaliacao = async () => {
        if (!avaliacaoParaExcluir || excluindoAvaliacao) return;

        setExcluindoAvaliacao(true);
        setErroExclusao('');

        try {
            await api.delete(`/api/student/${id}/evaluation/${avaliacaoParaExcluir}`, { withCredentials: true });
            const avaliacoesAtualizadas = avaliacoes.filter(av => av.id !== avaliacaoParaExcluir);
            setAvaliacoes(avaliacoesAtualizadas);
            navigate(location.pathname, {
                replace: true,
                state: {
                    ...location.state,
                    alunoSelecionado: { ...alunoSelecionado, avaliacoesRealizadas: avaliacoesAtualizadas }
                }
            });
            setModalConfirmacao(false);
            setAvaliacaoParaExcluir(null);
        } catch (error) {
            setErroExclusao('Não foi possível excluir a avaliação. Tente novamente.');
        } finally {
            setExcluindoAvaliacao(false);
        }
    };

    const handleFecharModalExcluirAluno = () => {
        if (excluindoAluno) return;
        setModalExcluirAluno(false);
        setErroExclusaoAluno('');
    };

    const handleConfirmarExclusaoAluno = async () => {
        if (excluindoAluno) return;

        setExcluindoAluno(true);
        setErroExclusaoAluno('');

        try {
            await api.delete(`/api/student/${id}`, { withCredentials: true });
            setModalExcluirAluno(false);
            navigate('/home');
        } catch (error) {
            setErroExclusaoAluno('Não foi possível excluir o aluno. Tente novamente.');
        } finally {
            setExcluindoAluno(false);
        }
    };

    return (
        <div>
            <Menu />
            <div className="avaliacao-container">
                <div className="avaliacao-content flex-row justify-center">

                    <div className="home-aluno-left-content">
                        <BlocoAluno
                            aluno={alunoSelecionado}
                            onAtualizarAlunoClick={handleAbrirEdicao}
                            onExcluirAlunoClick={handleExcluirAluno}
                            onCompararAvaliacoesClick={handleCompararAvaliacoes}
                        />
                        <EditarAluno
                            isOpen={isCadastroOpen}
                            onRequestClose={() => {
                                setIsCadastroOpen(false);
                                setAlunoParaEditar(null);
                            }}
                            alunoParaEditar={alunoParaEditar}
                        />
                    </div>

                    <div className="home-aluno-right-content">
                        {blocoAvaliacoesRealizadas && (
                            <div className="historico-container">
                                <div className="historico-content">
                                    <h2 className='bloco-aluno-title'>Avaliações Realizadas</h2>
                                    {avaliacoes.length > 0 ? (
                                        <table className="tabela-historico">
                                            <thead>
                                                <tr>
                                                    <th>Data</th>
                                                    <th>Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {avaliacoes.map(av => (
                                                    <tr key={av.id}>
                                                        <td>{tratarDataString(av.data)}</td>
                                                        <td>
                                                            <button className="btn-tabela" onClick={() => handleAbrirAvaliacao(av.id)}>Abrir avaliação</button>
                                                            <button className="btn-tabela btn-relatorio" onClick={() => setModalGerarRelatorio(av.id)}>Gerar relatório</button>
                                                            <button className="btn-tabela btn-excluir" onClick={() => handleAbrirModalExclusao(av.id)}>Excluir</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <p style={{ textAlign: "center", marginTop: "20px", color: "#666" }}>
                                            Nenhuma avaliação realizada para este aluno.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL DE RELATÓRIO — o treinador escolhe quais etapas realizadas entram no PDF. */}
            {modalGerarRelatorio && (
                <div className="modal-overlay">
                    <div className="modal-box">
                        <h3>Gerar Relatório</h3>
                        <p>
                            Selecione as etapas que devem entrar no relatório. Etapas não realizadas
                            nesta avaliação aparecem desabilitadas.
                        </p>

                        {carregandoRelatorio ? (
                            <p className="modal-aviso">Carregando dados da avaliação...</p>
                        ) : (
                            <div className="modal-grid">
                                {secoesDaAvaliacao.map(secao => {
                                    // A análise por IA só fica disponível depois que o servidor responde com texto.
                                    const ehAnalise = secao.chave === 'AnaliseIA';
                                    const disponivel = ehAnalise ? (secao.disponivel && (carregandoResumoIA || !!resumoIA)) : secao.disponivel;
                                    const rotulo = ehAnalise && carregandoResumoIA ? `${secao.nome} — gerando...` : secao.nome;
                                    return (
                                        <label
                                            key={secao.chave}
                                            className={`checkbox-item${disponivel ? '' : ' checkbox-item-desabilitado'}`}
                                        >
                                            <input
                                                type="checkbox"
                                                disabled={!disponivel}
                                                checked={!!secoesSelecionadas[secao.chave]}
                                                onChange={() => alternarSecao(secao.chave)}
                                            />
                                            {rotulo}
                                        </label>
                                    );
                                })}
                            </div>
                        )}

                        {erroResumoIA && <p className="modal-aviso">{erroResumoIA}</p>}

                        <div className="modal-footer">
                            <button onClick={handleFecharModalRelatorio} className="btn-cancelar">Cancelar</button>

                            {carregandoRelatorio || !evaluationDataParaPDF || !personalParaPDF ? (
                                <button className="btn-confirmar" disabled>
                                    {carregandoRelatorio ? 'Carregando dados...' : 'Aguardando dados...'}
                                </button>
                            ) : secoesSelecionadas.AnaliseIA && carregandoResumoIA ? (
                                <button className="btn-confirmar" disabled>Gerando análise...</button>
                            ) : chavesSelecionadas.length === 0 ? (
                                <button className="btn-confirmar" disabled>Selecione ao menos uma etapa</button>
                            ) : (
                                <PDFDownloadLink
                                    key={`${modalGerarRelatorio}-${chavesSelecionadas.join('-')}-${resumoIA ? 'com-analise' : 'sem-analise'}-${personalParaPDF?.photo ? 'com-foto' : 'sem-foto'}`}
                                    document={documentoPDF}
                                    fileName={`relatorio_${name.replace(/\s+/g, '_')}.pdf`}
                                    style={{ textDecoration: "none" }}
                                >
                                    {({ loading, error }) => {
                                        if (error) console.error("Erro ao gerar PDF:", error);
                                        return (
                                            <button className="btn-confirmar" disabled={loading}>
                                                {loading ? "Preparando..." : "Gerar Relatório"}
                                            </button>
                                        );
                                    }}
                                </PDFDownloadLink>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE COMPARAÇÃO — exige duas avaliações realizadas. */}
            {modalCompararAvaliacao && (
                <div className="modal-overlay">
                    <div className="modal-box">
                        <h3>Comparar avaliações</h3>

                        {avaliacoesOrdenadas.length < 2 ? (
                            <>
                                <p className="modal-aviso">
                                    São necessárias ao menos duas avaliações realizadas para gerar um relatório
                                    comparativo. Este aluno possui {avaliacoesOrdenadas.length === 0 ? 'nenhuma' : 'apenas uma'} avaliação
                                    até o momento.
                                </p>
                                <div className="modal-footer">
                                    <button onClick={handleFecharModalComparacao} className="btn-cancelar">Fechar</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p>Selecione de 2 a {LIMITE_COMPARACAO} avaliações para comparar ({comparacaoSelecionadas.length} selecionada{comparacaoSelecionadas.length === 1 ? '' : 's'}):</p>
                                <div className="lista-suspensa" ref={refListaComparacao}>
                                    <button
                                        type="button"
                                        className={`lista-suspensa-botao${listaComparacaoAberta ? ' aberta' : ''}`}
                                        onClick={() => setListaComparacaoAberta((aberta) => !aberta)}
                                    >
                                        <span>
                                            {comparacaoSelecionadas.length === 0
                                                ? 'Selecione as avaliações'
                                                : `${comparacaoSelecionadas.length} de ${LIMITE_COMPARACAO} avaliações selecionadas`}
                                        </span>
                                        <span className="lista-suspensa-seta">{listaComparacaoAberta ? '▲' : '▼'}</span>
                                    </button>

                                    {listaComparacaoAberta && (
                                        <ul className="lista-suspensa-opcoes">
                                            {avaliacoesOrdenadas.map((av) => {
                                                const marcada = comparacaoSelecionadas.includes(av.id);
                                                const bloqueada = !marcada && comparacaoSelecionadas.length >= LIMITE_COMPARACAO;
                                                return (
                                                    <li
                                                        key={av.id}
                                                        className={`lista-suspensa-opcao${marcada ? ' marcada' : ''}${bloqueada ? ' bloqueada' : ''}`}
                                                        onClick={() => { if (!bloqueada) alternarAvaliacaoComparacao(av.id); }}
                                                    >
                                                        <span className="lista-suspensa-marca">{marcada ? '✓' : ''}</span>
                                                        Avaliação de {tratarDataString(av.data)}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>

                                {comparacaoSelecionadas.length > 0 && (
                                    <div className="chips-selecao">
                                        {avaliacoesOrdenadas
                                            .filter((av) => comparacaoSelecionadas.includes(av.id))
                                            .map((av) => (
                                                <span key={av.id} className="chip-selecao">
                                                    {tratarDataString(av.data)}
                                                    <button type="button" className="chip-remover" title="Remover" onClick={() => alternarAvaliacaoComparacao(av.id)}>×</button>
                                                </span>
                                            ))}
                                    </div>
                                )}

                                {comparacaoSelecionadas.length === 1 && (
                                    <p className="modal-aviso">Selecione ao menos mais uma avaliação para comparar.</p>
                                )}
                                {erroComparacao && <p className="modal-aviso">{erroComparacao}</p>}
                                {dadosComparacao && !comparativoTemDados && (
                                    <p className="modal-aviso">
                                        As avaliações escolhidas não possuem dados comparáveis entre si. A comparação usa
                                        Antropometria, Composição Corporal, VO2 máx e Avaliação Neuromuscular.
                                    </p>
                                )}

                                <div className="modal-footer">
                                    <button onClick={handleFecharModalComparacao} className="btn-cancelar">Cancelar</button>

                                    {carregandoComparacao || !dadosComparacao || !personalParaPDF || !comparativoTemDados ? (
                                        <button className="btn-confirmar" disabled>
                                            {carregandoComparacao ? 'Carregando dados...' : 'Gerar relatório comparativo'}
                                        </button>
                                    ) : (
                                        <PDFDownloadLink
                                            key={`${comparacaoSelecionadas.join('-')}-${personalParaPDF?.photo ? 'com-foto' : 'sem-foto'}`}
                                            document={documentoComparativo}
                                            fileName={`evolucao_${name.replace(/\s+/g, '_')}.pdf`}
                                            style={{ textDecoration: "none" }}
                                        >
                                            {({ loading, error }) => {
                                                if (error) console.error("Erro ao gerar PDF comparativo:", error);
                                                return (
                                                    <button className="btn-confirmar" disabled={loading}>
                                                        {loading ? "Preparando..." : "Gerar relatório comparativo"}
                                                    </button>
                                                );
                                            }}
                                        </PDFDownloadLink>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <Modal
                isOpen={modalConfirmacao}
                onRequestClose={handleFecharModalExclusao}
                contentLabel="Confirmacao"
                className="modal-next"
                overlayClassName="popup-overlay"
            >
                <p className="popupNext-h1" style={{ marginBottom: 20 }}>Deseja mesmo <br />excluir a avaliação? </p>
                {erroExclusao && (
                    <p style={{ color: '#c0392b', textAlign: 'center', marginBottom: 10 }}>{erroExclusao}</p>
                )}
                <div className="sub-pagination-buttons">
                    <button className="next-prev-button" type="button" onClick={handleConfirmarExclusaoAvaliacao} disabled={excluindoAvaliacao}>
                        {excluindoAvaliacao ? 'Excluindo...' : 'Sim'}
                    </button>
                    <button className="next-prev-button" type="button" onClick={handleFecharModalExclusao} disabled={excluindoAvaliacao}>
                        Não
                    </button>
                </div>
            </Modal>
            <Modal
                isOpen={modalExcluirAluno}
                onRequestClose={handleFecharModalExcluirAluno}
                contentLabel="ExcluirAluno"
                className="modal-next"
                overlayClassName="popup-overlay"
            >
                <p className="popupNext-h1" style={{ marginBottom: 0 }}>Deseja mesmo <br />excluir o aluno? </p>
                <p classname="aviso-aluno-popup">
                    Esta ação também removerá todas as avaliações realizadas.
                </p>
                {erroExclusaoAluno && (
                    <p style={{ color: '#c0392b', textAlign: 'center', marginBottom: 10 }}>{erroExclusaoAluno}</p>
                )}
                <div className="sub-pagination-buttons">
                    <button className="next-prev-button" type="button" onClick={handleConfirmarExclusaoAluno} disabled={excluindoAluno}>
                        {excluindoAluno ? 'Excluindo...' : 'Sim'}
                    </button>
                    <button className="next-prev-button" type="button" onClick={handleFecharModalExcluirAluno} disabled={excluindoAluno}>
                        Não
                    </button>
                </div>
            </Modal>
        </div>
    );
}

export default HomeAluno;